import { IMAGE_BASE_URL } from "../services/api";

/**
 * Formats a number as currency with Turkish locale
 * Shows decimal places only when the value has a fractional part
 * @param value - The numeric value to format
 * @param currency - The currency code (default: "TRY")
 * @returns Formatted currency string
 */
export const formatCurrency = (
  value: number,
  currency: string = "TRY",
): string => {
  // Check if value has fractional part
  const hasFraction = value % 1 !== 0;

  return new Intl.NumberFormat("tr-TR", {
    style: "currency",
    currency: currency,
    minimumFractionDigits: hasFraction ? 2 : 0,
    maximumFractionDigits: 2,
  }).format(value);
};

/**
 * Formats a number with Turkish locale
 * Shows decimal places only when the value has a fractional part
 * @param value - The numeric value to format
 * @param maxDecimals - Maximum number of decimal places (default: 2)
 * @returns Formatted number string
 */
export const formatNumber = (
  value: number,
  maxDecimals: number = 2,
): string => {
  const num = Number(value);
  if (value == null || !isFinite(num)) return "-";
  const safeDecimals = Number.isFinite(maxDecimals) ? Math.max(0, Math.min(20, Math.round(maxDecimals))) : 2;

  // Check if value has fractional part
  const hasFraction = num % 1 !== 0;

  return new Intl.NumberFormat("tr-TR", {
    minimumFractionDigits: hasFraction ? Math.min(2, safeDecimals) : 0,
    maximumFractionDigits: safeDecimals,
  }).format(num);
};

/**
 * Normalizes Turkish characters for case-insensitive search
 * Converts both Turkish and English characters to lowercase equivalents
 * @param text - The text to normalize
 * @returns Normalized lowercase text
 */
export const normalizeTurkish = (text: string): string => {
  return text
    .toLocaleLowerCase("tr-TR")
    .replace(/ğ/g, "g")
    .replace(/ü/g, "u")
    .replace(/ş/g, "s")
    .replace(/ı/g, "i")
    .replace(/ö/g, "o")
    .replace(/ç/g, "c");
};

/**
 * Checks if text contains search term (case-insensitive, Turkish character aware)
 * @param text - The text to search in
 * @param searchTerm - The search term
 * @returns True if text contains search term
 */
export const turkishIncludes = (text: string, searchTerm: string): boolean => {
  return normalizeTurkish(text).includes(normalizeTurkish(searchTerm));
};

/**
 * Gets the full image URL by combining base URL with relative path
 * @param imageUrl - The image URL (can be relative or absolute)
 * @returns Full image URL
 */
export const getImageUrl = (imageUrl?: string): string => {
  if (!imageUrl) return "";

  // If already absolute URL, return as is
  if (imageUrl.startsWith("http://") || imageUrl.startsWith("https://")) {
    return imageUrl;
  }

  // Otherwise prepend base URL
  return `${IMAGE_BASE_URL}${imageUrl.startsWith("/") ? "" : "/"}${imageUrl}`;
};
